import {
  MAX_AUDIENCE_COMPANIES,
  MAX_BODY_LENGTH,
  MAX_LINK_LABEL_LENGTH,
  MAX_TITLE_LENGTH,
} from "shared/organization-messages";
import {
  OrganizationMessageEventAction,
  OrganizationMessageLinkType,
  OrganizationMessageSeverity,
  OrganizationMessageStatus,
  OrganizationMessageView,
  type OrganizationMessageAudienceInput,
  type OrganizationMessageCountsEntity,
  type OrganizationMessageContentInput,
  type OrganizationMessageFieldsFragment,
} from "../../generated/graphql";

export const NO_LINK = "NONE";

/** How the expiry field is set: severity default, a chosen time, or never. */
export type ExpiryMode = "DEFAULT" | "AT" | "NEVER";

export type MessageFormState = {
  readonly severity: OrganizationMessageSeverity;
  readonly title: string;
  readonly body: string;
  readonly linkType: OrganizationMessageLinkType | typeof NO_LINK;
  readonly linkTarget: string;
  readonly linkLabel: string;
  readonly expiryMode: ExpiryMode;
  /** `datetime-local` value, in the browser's time zone. */
  readonly expiresAt: string;
};

export const EMPTY_FORM: MessageFormState = {
  severity: OrganizationMessageSeverity.Info,
  title: "",
  body: "",
  linkType: NO_LINK,
  linkTarget: "",
  linkLabel: "",
  expiryMode: "DEFAULT",
  expiresAt: "",
};

export const DEFAULT_EXPIRY_LABEL: Record<OrganizationMessageSeverity, string> =
  {
    [OrganizationMessageSeverity.Info]: "14 days after it starts",
    [OrganizationMessageSeverity.Warning]: "3 days after it starts",
    [OrganizationMessageSeverity.Critical]: "never",
  };

/** `datetime-local` string for a Date, in the browser's time zone. */
export const toLocalInput = (value: Date): string => {
  const offsetMs = value.getTimezoneOffset() * 60 * 1000;
  return new Date(value.getTime() - offsetMs).toISOString().slice(0, 16);
};

export const formFromMessage = (
  message: OrganizationMessageFieldsFragment,
): MessageFormState => ({
  severity: message.severity,
  title: message.title,
  body: message.body,
  linkType: message.linkType ?? NO_LINK,
  linkTarget: message.linkTarget ?? "",
  linkLabel: message.linkLabel ?? "",
  expiryMode: message.usesDefaultExpiry
    ? "DEFAULT"
    : message.expiresAt === null
      ? "NEVER"
      : "AT",
  expiresAt:
    message.usesDefaultExpiry || message.expiresAt === null
      ? ""
      : toLocalInput(new Date(String(message.expiresAt))),
});

/** The first problem with the form, or `null` when it can be saved. */
export const formError = (form: MessageFormState): string | null => {
  if (form.title.trim().length === 0 && form.body.trim().length === 0) {
    return "Write the message.";
  }
  if (form.title.length > MAX_TITLE_LENGTH) {
    return `The title is limited to ${MAX_TITLE_LENGTH} characters.`;
  }
  if (form.body.length > MAX_BODY_LENGTH) {
    return `The body is limited to ${MAX_BODY_LENGTH} characters.`;
  }
  if (form.linkType !== NO_LINK) {
    if (
      form.linkTarget.trim().length === 0 ||
      form.linkLabel.trim().length === 0
    ) {
      return "A link needs a target and a label.";
    }
    if (form.linkLabel.length > MAX_LINK_LABEL_LENGTH) {
      return `The link label is limited to ${MAX_LINK_LABEL_LENGTH} characters.`;
    }
  }
  if (form.expiryMode === "AT" && form.expiresAt === "") {
    return "Choose when the message expires.";
  }
  return null;
};

export const contentInput = (
  form: MessageFormState,
): OrganizationMessageContentInput => ({
  severity: form.severity,
  title: form.title.trim(),
  body: form.body.trim(),
  link:
    form.linkType === NO_LINK
      ? null
      : {
          type: form.linkType,
          target: form.linkTarget.trim(),
          label: form.linkLabel.trim(),
        },
  ...(form.expiryMode === "DEFAULT"
    ? {}
    : {
        expiresAt:
          form.expiryMode === "NEVER" ? null : new Date(form.expiresAt),
      }),
});

export const LINK_TARGET_HELP: Record<OrganizationMessageLinkType, string> = {
  [OrganizationMessageLinkType.Documentation]:
    "The slug of a published documentation article.",
  [OrganizationMessageLinkType.Internal]:
    'An app path such as "/settings/documentation".',
  [OrganizationMessageLinkType.External]: "An https URL.",
};

export type MessageReach = {
  readonly companyCount: number;
  readonly userCount: number;
  readonly bellEnabledCompanyCount: number;
};

/**
 * What staff are told before sending. INFO lives only in the bell, so it
 * reaches only the companies with the bell enabled; WARNING and CRITICAL also
 * show a banner everywhere.
 */
export const reachSummary = (
  severity: OrganizationMessageSeverity,
  reach: MessageReach,
): string =>
  severity === OrganizationMessageSeverity.Info
    ? `INFO shows only in the bell: it reaches ${reach.bellEnabledCompanyCount} of ${reach.companyCount} companies (the ones with the bell enabled).`
    : `The banner reaches all ${reach.companyCount} companies and their ${reach.userCount} users; the bell shows it in ${reach.bellEnabledCompanyCount} of them.`;

/** ACTIVE but past its expiry: shown to staff as EXPIRED. */
export const displayStatus = (
  message: Pick<OrganizationMessageFieldsFragment, "status" | "expiresAt">,
  now: Date = new Date(),
): string =>
  message.status === OrganizationMessageStatus.Active &&
  message.expiresAt !== null &&
  new Date(String(message.expiresAt)) <= now
    ? "EXPIRED"
    : message.status;

export type CompanyOption = { readonly uuid: string; readonly name: string };

/** Who a message goes to, as the composer edits it. */
export type AudienceState = {
  readonly allCompanies: boolean;
  readonly companies: readonly CompanyOption[];
};

export const EMPTY_AUDIENCE: AudienceState = {
  allCompanies: false,
  companies: [],
};

export const audienceFromMessage = (
  message: Pick<
    OrganizationMessageFieldsFragment,
    "allCompanies" | "companies"
  >,
): AudienceState => ({
  allCompanies: message.allCompanies,
  companies: message.companies,
});

export const audienceError = (audience: AudienceState): string | null => {
  if (audience.allCompanies) {
    return null;
  }
  if (audience.companies.length === 0) {
    return "Choose at least one company, or send to all companies.";
  }
  if (audience.companies.length > MAX_AUDIENCE_COMPANIES) {
    return `Choose at most ${MAX_AUDIENCE_COMPANIES} companies, or send to all companies.`;
  }
  return null;
};

export const audienceInput = (
  audience: AudienceState,
): OrganizationMessageAudienceInput => ({
  allCompanies: audience.allCompanies,
  companyUuids: audience.allCompanies
    ? []
    : audience.companies.map((company) => company.uuid),
});

const NAMES_SHOWN = 2;

/** "All companies", "Acme", "Acme and Beta", or "Acme, Beta and 3 more". */
export const audienceLabel = (audience: AudienceState): string => {
  if (audience.allCompanies) {
    return "All companies";
  }
  const names = audience.companies.map((company) => company.name);
  if (names.length <= NAMES_SHOWN) {
    return names.join(" and ");
  }
  return `${names.slice(0, NAMES_SHOWN).join(", ")} and ${names.length - NAMES_SHOWN} more`;
};

/** The headline staff recognise a message by: its title, or its body. */
export const messageHeadline = (
  message: Pick<OrganizationMessageFieldsFragment, "title" | "body">,
): string => (message.title.trim().length > 0 ? message.title : message.body);

/** List tabs: what staff look for, not the raw lifecycle statuses. */
export type MessageTab = "LIVE" | "SCHEDULED" | "DRAFTS" | "ENDED" | "ALL";

/**
 * Each tab lists one server-side view, so a tab never depends on how many
 * pages are loaded. "All" sends no view.
 */
export const MESSAGE_TABS: ReadonlyArray<{
  value: MessageTab;
  label: string;
  view: OrganizationMessageView | undefined;
}> = [
  { value: "LIVE", label: "Live", view: OrganizationMessageView.Live },
  {
    value: "SCHEDULED",
    label: "Scheduled",
    view: OrganizationMessageView.Scheduled,
  },
  { value: "DRAFTS", label: "Drafts", view: OrganizationMessageView.Drafts },
  { value: "ENDED", label: "Ended", view: OrganizationMessageView.Ended },
  { value: "ALL", label: "All", view: undefined },
];

export type MessageCounts = Omit<OrganizationMessageCountsEntity, "__typename">;

/** Every message is in exactly one view, so "All" is their sum. */
export const tabCount = (tab: MessageTab, counts: MessageCounts): number => {
  switch (tab) {
    case "LIVE": {
      return counts.live;
    }
    case "SCHEDULED": {
      return counts.scheduled;
    }
    case "DRAFTS": {
      return counts.drafts;
    }
    case "ENDED": {
      return counts.ended;
    }
    case "ALL": {
      return counts.live + counts.scheduled + counts.drafts + counts.ended;
    }
  }
};

/** What staff see for a status: plain words and a colour. */
export const STATUS_DISPLAY: Record<
  string,
  {
    label: string;
    color: "default" | "success" | "info" | "warning" | "error";
  }
> = {
  DRAFT: { label: "Draft", color: "default" },
  SCHEDULED: { label: "Scheduled", color: "info" },
  ACTIVE: { label: "Live", color: "success" },
  EXPIRED: { label: "Expired", color: "default" },
  RESOLVED: { label: "Resolved", color: "default" },
  WITHDRAWN: { label: "Withdrawn", color: "default" },
};

export const SEVERITY_DISPLAY: Record<
  OrganizationMessageSeverity,
  { label: string; color: "info" | "warning" | "error"; reach: string }
> = {
  [OrganizationMessageSeverity.Info]: {
    label: "Info",
    color: "info",
    reach: "Bell only",
  },
  [OrganizationMessageSeverity.Warning]: {
    label: "Warning",
    color: "warning",
    reach: "Bell and a banner users can close",
  },
  [OrganizationMessageSeverity.Critical]: {
    label: "Critical",
    color: "error",
    reach: "Bell and a banner that returns on every page load",
  },
};

/** History verbs, as staff would say them. */
export const ACTION_LABEL: Record<OrganizationMessageEventAction, string> = {
  [OrganizationMessageEventAction.Created]: "Created",
  [OrganizationMessageEventAction.Edited]: "Edited",
  [OrganizationMessageEventAction.Published]: "Sent",
  [OrganizationMessageEventAction.Scheduled]: "Scheduled",
  [OrganizationMessageEventAction.Rescheduled]: "Rescheduled",
  [OrganizationMessageEventAction.Cancelled]: "Cancelled",
  [OrganizationMessageEventAction.Started]: "Started showing",
  [OrganizationMessageEventAction.Resolved]: "Resolved",
  [OrganizationMessageEventAction.Withdrawn]: "Withdrawn",
};

export const formatDateTime = (value: unknown): string =>
  typeof value === "string" || value instanceof Date
    ? new Date(value).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "-";

/** The one date that matters for where the message is in its life. */
export const statusDateLabel = (
  message: Pick<
    OrganizationMessageFieldsFragment,
    | "status"
    | "createdAt"
    | "startsAt"
    | "publishedAt"
    | "expiresAt"
    | "resolvedAt"
    | "withdrawnAt"
  >,
  now: Date = new Date(),
): string => {
  switch (displayStatus(message, now)) {
    case "ACTIVE": {
      return `Live since ${formatDateTime(message.publishedAt)}`;
    }
    case "SCHEDULED": {
      return `Starts ${formatDateTime(message.startsAt)}`;
    }
    case "EXPIRED": {
      return `Expired ${formatDateTime(message.expiresAt)}`;
    }
    case "RESOLVED": {
      return `Resolved ${formatDateTime(message.resolvedAt)}`;
    }
    case "WITHDRAWN": {
      return `Withdrawn ${formatDateTime(message.withdrawnAt)}`;
    }
    default: {
      return `Created ${formatDateTime(message.createdAt)}`;
    }
  }
};
